import { useDependents } from '@cascade/react/dependents';
import { useI18n } from '@cascade/react/i18n';
import {
  Badge,
  Button,
  FeedbackText,
  Table,
  Text,
  Tooltip,
  useToast,
} from '@cascade/ui-library';
import type { TTableColumn } from '@cascade/ui-library';
import { useMemo, useState } from 'react';
import { AddDependentDialog } from './add-dependent-dialog/index.ts';
import { buildDependentRows } from './Dependents.helpers.ts';
import classes from './Dependents.module.scss';
import type { TDependentRow } from './Dependents.types.ts';
import { ConfirmDialog } from '#components/confirm-dialog/index.ts';
import { Page } from '#components/page/index.ts';

const STATUS_COLUMN_WIDTH = 160;
const ACTIONS_COLUMN_WIDTH = 64;
const statusBadgeVariants = {
  active: 'success-lighter',
  pending: 'warning-lighter',
} as const;

export const Dependents = () => {
  const { translate, translateError } = useI18n();
  const { toast } = useToast();
  const {
    activeDependents,
    pendingInvitations,
    isLoadingDependents,
    dependentsError,
    hasReachedDependentLimit,
    isRemovingDependent,
    removeDependent,
  } = useDependents();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [removingRow, setRemovingRow] = useState<TDependentRow>();

  const rows = useMemo(
    () => buildDependentRows(activeDependents, pendingInvitations),
    [activeDependents, pendingInvitations],
  );

  const isListReady = !isLoadingDependents && dependentsError === undefined;

  const columns: TTableColumn<TDependentRow>[] = [
    {
      id: 'name',
      label: translate('dependents.name'),
      width: 'fill',
      renderer: (row) => (
        <Text
          type='body-2'
          color={row.name ? undefined : 'text-60'}
          content={row.name ?? translate('dependents.unnamed')}
          compact
        />
      ),
    },
    {
      id: 'email',
      label: translate('dependents.email'),
      width: 'fill',
      renderer: (row) => <Text type='body-2' content={row.email} compact />,
    },
    {
      id: 'status',
      label: translate('dependents.status'),
      width: STATUS_COLUMN_WIDTH,
      renderer: (row) => (
        <Badge
          label={translate(`dependents.status.${row.status}`)}
          variant={statusBadgeVariants[row.status]}
          size='sm'
        />
      ),
    },
    {
      id: 'actions',
      label: '',
      width: ACTIONS_COLUMN_WIDTH,
      align: 'right',
      renderer: (row) => (
        <Button
          variant='danger'
          size='sm'
          iconType='trash'
          iconOnly
          ghost
          label={translate('dependents.remove', {
            name: row.name ?? row.email,
          })}
          disabled={isRemovingDependent}
          onClick={() => setRemovingRow(row)}
        />
      ),
    },
  ];

  const openAddDialog = () => setIsAddDialogOpen(true);
  const closeAddDialog = () => setIsAddDialogOpen(false);
  const closeRemoveDialog = () => setRemovingRow(undefined);

  const finishAdding = (email: string) => {
    closeAddDialog();
    toast({
      title: translate('dependents.added', { email }),
      variant: 'success',
    });
  };

  const confirmRemove = () => {
    if (!removingRow) {
      return;
    }

    removeDependent(removingRow.email)
      .then(() =>
        toast({ title: translate('dependents.removed'), variant: 'success' }),
      )
      .catch((error: unknown) =>
        toast({
          title: translate('dependents.removeFailed'),
          description: translateError(error),
          variant: 'danger',
        }),
      )
      .finally(closeRemoveDialog);
  };

  const addButton = (
    <Button
      variant='primary'
      size='sm'
      iconType='user-plus'
      label={translate('dependents.add')}
      disabled={!isListReady || hasReachedDependentLimit}
      onClick={openAddDialog}
    />
  );

  return (
    <Page iconType='users' title={translate('dependents.title')}>
      <div className={classes.toolbar}>
        {hasReachedDependentLimit ? (
          <Tooltip content={translate('dependents.add.limit')} placement='left'>
            {addButton}
          </Tooltip>
        ) : (
          addButton
        )}
      </div>
      {dependentsError ? (
        <FeedbackText
          feedbackLevel='danger'
          feedbackText={translateError(dependentsError)}
        />
      ) : null}
      <Table
        aria-label={translate('dependents.table')}
        columns={columns}
        data={rows}
        isLoading={isLoadingDependents}
        placeholder={translate('dependents.none')}
        className={classes.table}
      />
      {isAddDialogOpen ? (
        <AddDependentDialog onAdded={finishAdding} onClose={closeAddDialog} />
      ) : null}
      {removingRow ? (
        <ConfirmDialog
          title={translate('dependents.remove.title')}
          description={translate('dependents.remove.description', {
            name: removingRow.name ?? removingRow.email,
          })}
          confirmLabel={translate('dependents.remove.confirm')}
          cancelLabel={translate('dependents.remove.cancel')}
          isDestructive
          isPending={isRemovingDependent}
          onConfirm={confirmRemove}
          onClose={closeRemoveDialog}
        />
      ) : null}
    </Page>
  );
};
